/** Training hub — courses for judges, producers, competition managers. */

import { useEffect, useState } from "react";
import { Player } from "@remotion/player";
import { api } from "@/api/client";
import { useAuthStore } from "@/stores/auth";
import { Button } from "@/components/ui/Button";
import { JudgeCourse1 } from "@/remotion/compositions/JudgeCourse1";
import { JudgeCourse2 } from "@/remotion/compositions/JudgeCourse2";
import { ScoreRiderGuide } from "@/remotion/compositions/guides/ScoreRiderGuide";
import { ScoringModelsGuide } from "@/remotion/compositions/guides/ScoringModelsGuide";
import { JudgeCourse4 } from "@/remotion/compositions/JudgeCourse4";
import { ProducerCourse1 } from "@/remotion/compositions/ProducerCourse1";
import { ProducerCourse2 } from "@/remotion/compositions/ProducerCourse2";
import { SetupOBSGuide } from "@/remotion/compositions/guides/SetupOBSGuide";
import { GoLiveGuide } from "@/remotion/compositions/guides/GoLiveGuide";
import { ManageAttemptsGuide } from "@/remotion/compositions/guides/ManageAttemptsGuide";
import { PMCourse1 } from "@/remotion/compositions/PMCourse1";
import { PMCourse2 } from "@/remotion/compositions/PMCourse2";
import { PMCourse7 } from "@/remotion/compositions/PMCourse7";
import { CreateProjectGuide } from "@/remotion/compositions/guides/CreateProjectGuide";
import { ManageRidersGuide } from "@/remotion/compositions/guides/ManageRidersGuide";
import { SetupCompetitionGuide } from "@/remotion/compositions/guides/SetupCompetitionGuide";
import { useNavigate, useParams } from "react-router-dom";
import type { CourseCompletion } from "@/types";

interface CourseClip {
  title: string;
  component: React.FC;
  durationInFrames: number;
}

const COURSES = [
  {
    type: "judge" as const,
    title: "Judge Certification",
    description: "Learn how to score riders accurately, understand scoring models, and use the judge interface.",
    clips: [
      { title: "The Judge Interface", component: JudgeCourse1, durationInFrames: 480 },
      { title: "Scoring Riders", component: JudgeCourse2, durationInFrames: 390 },
      { title: "Score a Rider", component: ScoreRiderGuide, durationInFrames: 360 },
      { title: "Scoring Models", component: ScoringModelsGuide, durationInFrames: 450 },
      { title: "How Scoring Works", component: JudgeCourse4, durationInFrames: 720 },
    ] as CourseClip[],
    steps: [],
  },
  {
    type: "producer" as const,
    title: "Producer Certification",
    description: "Master the live overlay controls, OBS setup, and real-time broadcast management.",
    clips: [
      { title: "Going Live", component: ProducerCourse1, durationInFrames: 1360 },
      { title: "Controlling the Broadcast", component: ProducerCourse2, durationInFrames: 600 },
      { title: "Set Up OBS Overlay", component: SetupOBSGuide, durationInFrames: 550 },
      { title: "Go Live with Overlay", component: GoLiveGuide, durationInFrames: 1200 },
      { title: "Managing Attempts", component: ManageAttemptsGuide, durationInFrames: 720 },
    ] as CourseClip[],
    steps: [
      "Select your heat and click Go Live",
      "Focus a rider to spotlight them on the broadcast",
      "Show the leaderboard to reveal rankings",
      "Click Go Dark to hide the overlay when done",
      "Coordinate with judges for timing and flow",
      "In OBS, add a new Browser Source — set width to 1920 and height to 1080",
      "Paste the overlay URL — the overlay has a transparent background",
      "Use Focus to highlight a specific rider on the broadcast",
      "Use Scores to show a rider's individual judge scores",
      "Use Show Leaderboard to display the full results table",
    ],
  },
  {
    type: "competition_manager" as const,
    title: "Competition Manager Certification",
    description: "Set up competitions, manage teams, configure scoring, and run events end-to-end.",
    clips: [
      { title: "Creating a Competition", component: PMCourse1, durationInFrames: 650 },
      { title: "Setting Up Your Event", component: PMCourse2, durationInFrames: 750 },
      { title: "Setup & Invite Team", component: CreateProjectGuide, durationInFrames: 600 },
      { title: "Manage Riders", component: ManageRidersGuide, durationInFrames: 750 },
      { title: "Setup Divisions & Heats", component: SetupCompetitionGuide, durationInFrames: 840 },
      { title: "Settings & Overlay Styles", component: PMCourse7, durationInFrames: 1520 },
    ] as CourseClip[],
    steps: [
      "Create a competition and choose a scoring model",
      "Set the number of judges (1-10)",
      "Create heats to organize competition rounds",
      "Assign riders from your roster to each heat",
      "Invite judges and producers by email",
      "The judge will receive an email with a link to accept",
      "Go to Riders in the top nav — this is your account-level roster",
      "Click Add Rider and fill in their details — upload a photo (max 5MB)",
      "Riders are reusable across all your competitions",
      "In competition Setup, add riders from your roster to specific heats",
    ],
  },
];

export function TrainingPage() {
  const user = useAuthStore((s) => s.user);
  const [completions, setCompletions] = useState<CourseCompletion[]>([]);
  const navigate = useNavigate();

  useEffect(() => {
    if (!user) return;
    api.get<CourseCompletion[]>("/api/courses/completions").then(setCompletions);
  }, [user]);

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Training</h1>
          <p className="page-subtitle">
            Complete courses to earn your Raley certification. Each takes about 5 minutes.
          </p>
        </div>
      </div>

      <div className="flex-col gap-6">
        {COURSES.map((course) => {
          const completion = completions.find((c) => c.course_type === course.type);
          const completed = !!completion;

          return (
            <div key={course.type} className="card">
              <div className="flex items-center justify-between">
                <div>
                  <div className="card-title">{course.title}</div>
                  <p className="text-sm text-muted" style={{ marginTop: 4 }}>
                    {course.description}
                  </p>
                </div>
                <div className="flex gap-3 items-center">
                  {completed && (
                    <span className="badge" style={{ background: "var(--c-success)", color: "#fff" }}>
                      Completed
                    </span>
                  )}
                  {completed && completion?.certificate_id && (
                    <a
                      href={`${import.meta.env.VITE_API_URL ?? ""}/api/courses/certificate/${completion.certificate_id}`}
                      target="_blank"
                      rel="noreferrer"
                    >
                      <Button variant="secondary" size="sm">
                        Download Certificate
                      </Button>
                    </a>
                  )}
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => navigate(`/training/${course.type}`)}
                  >
                    {completed ? "Review" : "Start"}
                  </Button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Contact */}
      <div className="card" style={{ marginTop: 32, textAlign: "center", padding: 32 }}>
        <div className="card-title">Need more help?</div>
        <p className="text-sm text-muted" style={{ marginTop: 8 }}>
          Email us at{" "}
          <a href="mailto:raley@koda-software.com" className="text-mono">
            raley@koda-software.com
          </a>
        </p>
      </div>
    </div>
  );
}

/** Individual course view — video player, clip navigation, completion. */
export function CoursePage() {
  const { courseType } = useParams<{ courseType: string }>();
  const user = useAuthStore((s) => s.user);
  const [completions, setCompletions] = useState<CourseCompletion[]>([]);
  const [clipIndex, setClipIndex] = useState(0);
  const navigate = useNavigate();

  const course = COURSES.find((c) => c.type === courseType);

  useEffect(() => {
    if (!user) return;
    api.get<CourseCompletion[]>("/api/courses/completions").then(setCompletions);
  }, [user]);

  if (!course) {
    return (
      <div>
        <Button variant="ghost" size="sm" onClick={() => navigate("/training")}>
          &larr; Back to Training
        </Button>
        <div className="empty-state" style={{ marginTop: 24 }}>
          <div className="empty-state-title">Course not found</div>
        </div>
      </div>
    );
  }

  const completed = completions.some((c) => c.course_type === course.type);
  const completion = completions.find((c) => c.course_type === course.type);
  const clip = course.clips[clipIndex];



  return (
    <div>
      <div className="page-header">
        <div>
          <Button variant="ghost" size="sm" onClick={() => navigate("/training")} style={{ marginBottom: 8 }}>
            &larr; Back to Training
          </Button>
          <h1 className="page-title">{course.title}</h1>
          <p className="page-subtitle">{course.description}</p>
        </div>
        {completed && (
          <span className="badge" style={{ background: "var(--c-success)", color: "#fff" }}>
            Completed
          </span>
        )}
      </div>

      {/* Video player */}
      <div style={{ border: "1px solid var(--c-border)", overflow: "hidden", marginBottom: 16, cursor: "pointer" }}>
        <Player
          key={`${course.type}-${clipIndex}`}
          component={clip.component}
          compositionWidth={1920}
          compositionHeight={1080}
          durationInFrames={clip.durationInFrames}
          fps={30}
          style={{ width: "100%", aspectRatio: "16/9", cursor: "pointer" }}
          controls
          acknowledgeRemotionLicense
          numberOfSharedAudioTags={0}
        />
      </div>

      {/* Clip navigation */}
      <div style={{
        display: "flex", justifyContent: "space-between", alignItems: "center",
        padding: "12px 0", marginBottom: 8,
      }}>
        <Button
          variant="secondary"
          disabled={clipIndex === 0}
          onClick={() => setClipIndex(clipIndex - 1)}
          style={{ minWidth: 120 }}
        >
          &larr; Previous
        </Button>
        <span style={{ fontSize: 14, fontWeight: 600, color: "var(--c-text)" }}>
          {clip.title}
          <span style={{ color: "var(--c-text-dim)", fontWeight: 400, marginLeft: 8, fontSize: 12, fontFamily: "var(--font-mono)" }}>
            {clipIndex + 1}/{course.clips.length}
          </span>
        </span>
        <Button
          variant="secondary"
          disabled={clipIndex === course.clips.length - 1}
          onClick={() => setClipIndex(clipIndex + 1)}
          style={{ minWidth: 120 }}
        >
          Next &rarr;
        </Button>
      </div>

      {/* Completion / Quiz trigger */}
      <div style={{ marginTop: 24, display: "flex", gap: 12, alignItems: "center" }}>
        {!completed ? (
          <Button onClick={() => navigate(`/quiz/${course.type}`)}>
            Take Quiz to Get Certified
          </Button>
        ) : (
          <div className="flex gap-3 items-center">
            <a
              href={`${import.meta.env.VITE_API_URL ?? ""}/api/courses/certificate/${completion?.certificate_id}`}
              target="_blank"
              rel="noreferrer"
            >
              <Button variant="secondary">Download Certificate (PDF)</Button>
            </a>
            <Button variant="ghost" size="sm" onClick={() => navigate(`/quiz/${course.type}`)}>
              Retake Quiz (Practice)
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
